import { config, GENDER, SIBLING_TYPE } from '../../pedigree/config';
import {
  relationship,
  getRelationshipForNewPerson
} from '../../pedigree/utils';
import {
  adoptableChildrenForNewSpouse,
  addSiblingsToRecords,
  addChildrenToRecords
} from '../../pedigree/edgeIntegrity';
import { mintObjectIdHex } from '../../pedigree/saveReconciliation';
import {
  GENDER_IDENTITY,
  spouseRelations
} from 'src/components/questionnaire/Constants';

const getNextId = (dataset) => {
  if (!dataset || dataset.length === 0) return 1;

  const numericIds = dataset.map((p) => {
    if (typeof p.pid === 'number') return p.pid;
    else return 0;
  });

  return Math.max(...numericIds) + 1;
};

export const createPerson = (
  gender,
  user_id,
  dataset = [],
  relationshipValue = ''
) => {
  const pid = getNextId(dataset);
  return {
    user_id,
    pid,
    uid: mintObjectIdHex(),
    gender,
    genderIdentity: GENDER_IDENTITY[gender] ?? GENDER_IDENTITY.unknown,
    motherId: null,
    fatherId: null,
    spouseIds: [],
    childrenIds: [],
    siblingIds: [],
    relationship: relationshipValue,
    isDynamicallyAdded: true
  };
};

const ownerPidOf = (node) =>
  node.isProxy && node.proxyOf != null ? node.proxyOf : node.pid;

const isNode = (node) => node?.kind === config.NODE_TYPE.NODE;

export const addParents = (members, node, user) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  const fatherRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.father
  );
  const motherRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.mother
  );

  const prev = members;
  const father = createPerson('male', user._id, prev, fatherRel);
  const mother = createPerson('female', user._id, [...prev, father], motherRel);

  father.spouseIds.push(mother.pid);
  mother.spouseIds.push(father.pid);
  father.spouseRelationships = {
    [mother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  mother.spouseRelationships = {
    [father.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  father.childrenIds.push(ownerPid);
  mother.childrenIds.push(ownerPid);

  return prev
    .map((person) =>
      person.pid === ownerPid
        ? { ...person, fatherId: father.pid, motherId: mother.pid }
        : person
    )
    .concat([father, mother]);
};

export const addSpouse = (members, node, user) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  const spouseRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.spouse
  );

  const prev = members;
  const self = prev.find((person) => person.pid === ownerPid);
  if (!self) return prev;

  const spouse = createPerson(
    self.gender === 'male' ? 'female' : 'male',
    user._id,
    prev,
    spouseRel
  );

  const adoptable = new Set(adoptableChildrenForNewSpouse(prev, self));

  const arr = prev.map((person) => {
    if (person.pid === ownerPid) {
      return {
        ...person,
        spouseIds: [...person.spouseIds, spouse.pid],
        spouseRelationships: {
          ...(person.spouseRelationships || {}),
          [spouse.pid]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        }
      };
    }

    if (adoptable.has(person.pid)) {
      if (self.gender === 'male') {
        return {
          ...person,
          motherId: spouse.pid
        };
      } else {
        return {
          ...person,
          fatherId: spouse.pid
        };
      }
    }

    return person;
  });

  const newSpouse = {
    ...spouse,
    spouseIds: [ownerPid],
    spouseRelationships: {
      [ownerPid]: {
        parentRelationship: spouseRelations.MARRIED,
        isConsanguineous: false
      }
    },
    childrenIds: [...adoptable]
  };

  const nodeIndex = arr.findIndex((member) => member.pid === ownerPid);

  let insertIndex = nodeIndex + 1;
  (self.spouseIds || []).forEach((existingSpouseId) => {
    const spouseIdx = arr.findIndex((m) => m.pid === existingSpouseId);
    if (spouseIdx !== -1 && spouseIdx + 1 > insertIndex) {
      insertIndex = spouseIdx + 1;
    }
  });
  arr.splice(insertIndex, 0, newSpouse);
  return arr;
};

export const addGrandparents = (members, node, user) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  const prev = members;
  const self = prev.find((p) => p.pid === ownerPid) || node;
  const selfHasBothParents = !!(self.fatherId && self.motherId);
  const newPeople = [];
  let updatedData = [...prev];
  let fatherId = self.fatherId ?? null;
  let motherId = self.motherId ?? null;

  //Create parents if they don't exist
  if (!selfHasBothParents) {
    if (!fatherId) {
      const father = createPerson(
        'male',
        user._id,
        [...updatedData, ...newPeople],
        getRelationshipForNewPerson(node, user, relationship.father)
      );
      fatherId = father.pid;
      newPeople.push(father);
    }

    if (!motherId) {
      const mother = createPerson(
        'female',
        user._id,
        [...updatedData, ...newPeople],
        getRelationshipForNewPerson(node, user, relationship.mother)
      );
      motherId = mother.pid;
      newPeople.push(mother);
    }

    if (!self.fatherId && !self.motherId) {
      const father = newPeople.find((p) => p.pid === fatherId);
      const mother = newPeople.find((p) => p.pid === motherId);
      if (father && mother) {
        father.spouseIds.push(motherId);
        mother.spouseIds.push(fatherId);
        father.spouseRelationships = {
          [motherId]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        };
        mother.spouseRelationships = {
          [fatherId]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        };
      }
    }

    // Link parents to child
    const father =
      newPeople.find((p) => p.pid === fatherId) ||
      updatedData.find((p) => p.pid === fatherId);
    const mother =
      newPeople.find((p) => p.pid === motherId) ||
      updatedData.find((p) => p.pid === motherId);

    if (father && !father.childrenIds.includes(ownerPid)) {
      father.childrenIds.push(ownerPid);
    }
    if (mother && !mother.childrenIds.includes(ownerPid)) {
      mother.childrenIds.push(ownerPid);
    }

    updatedData = updatedData.map((person) => {
      if (person.pid === ownerPid) {
        return {
          ...person,
          fatherId: fatherId,
          motherId: motherId
        };
      }
      return person;
    });
  }

  // Add  grandparents : father-side
  const paternalGrandfather = createPerson(
    'male',
    user._id,
    [...updatedData, ...newPeople],
    relationship.fathersFather
  );
  newPeople.push(paternalGrandfather);

  const paternalGrandmother = createPerson(
    'female',
    user._id,
    [...updatedData, ...newPeople],
    relationship.fathersMother
  );
  newPeople.push(paternalGrandmother);

  paternalGrandfather.spouseIds.push(paternalGrandmother.pid);
  paternalGrandmother.spouseIds.push(paternalGrandfather.pid);
  paternalGrandfather.spouseRelationships = {
    [paternalGrandmother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  paternalGrandmother.spouseRelationships = {
    [paternalGrandfather.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };

  paternalGrandfather.childrenIds.push(fatherId);
  paternalGrandmother.childrenIds.push(fatherId);

  // Add  grandparents: mother-side
  const maternalGrandfather = createPerson(
    'male',
    user._id,
    [...updatedData, ...newPeople],
    relationship.mothersFather
  );
  newPeople.push(maternalGrandfather);

  const maternalGrandmother = createPerson(
    'female',
    user._id,
    [...updatedData, ...newPeople],
    relationship.mothersMother
  );
  newPeople.push(maternalGrandmother);

  maternalGrandfather.spouseIds.push(maternalGrandmother.pid);
  maternalGrandmother.spouseIds.push(maternalGrandfather.pid);
  maternalGrandfather.spouseRelationships = {
    [maternalGrandmother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  maternalGrandmother.spouseRelationships = {
    [maternalGrandfather.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };

  maternalGrandfather.childrenIds.push(motherId);
  maternalGrandmother.childrenIds.push(motherId);

  newPeople.forEach((person) => {
    if (person.pid === fatherId) {
      person.fatherId = paternalGrandfather.pid;
      person.motherId = paternalGrandmother.pid;
    }
    if (person.pid === motherId) {
      person.fatherId = maternalGrandfather.pid;
      person.motherId = maternalGrandmother.pid;
    }
  });

  updatedData = updatedData.map((person) => {
    if (person.pid === fatherId) {
      return {
        ...person,
        fatherId: paternalGrandfather.pid,
        motherId: paternalGrandmother.pid
      };
    }
    if (person.pid === motherId) {
      return {
        ...person,
        fatherId: maternalGrandfather.pid,
        motherId: maternalGrandmother.pid
      };
    }
    return person;
  });

  return [...updatedData, ...newPeople];
};

export const addChildren = (members, node, user, gender, count = 1) => {
  if (!isNode(node)) return members;
  if (count < 1) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.son : relationship.daughter
  );
  return addChildrenToRecords(members, ownerPid, {
    count,
    makeChild: () => createPerson(gender, user._id, [], relationshipValue)
  });
};

export const addSiblings = (members, node, user, gender, count = 1) => {
  if (!isNode(node)) return members;
  const hasParents = node.fatherId || node.motherId;
  if (!hasParents || count < 1) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.fullBrother : relationship.fullSister
  );
  return addSiblingsToRecords(members, ownerPid, {
    count,
    makeSibling: () => createPerson(gender, user._id, [], relationshipValue)
  });
};

// Single sibling with twin support (context-menu SiblingMenu). Mirrors
// addSibling: only the kind guard — no parent requirement.
export const addSibling = (
  members,
  node,
  user,
  { siblingType, gender, twinType }
) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.fullBrother : relationship.fullSister
  );
  const twinSubGroupId = `twin-group-${Math.random().toString(36).slice(2, 8)}`;
  return addSiblingsToRecords(members, ownerPid, {
    count: 1,
    isTwin: siblingType === SIBLING_TYPE.twin,
    siblingType,
    twinType,
    twinSubGroupId,
    makeSibling: () => createPerson(gender, user._id, [], relationshipValue)
  });
};

export const addUnknown = (members, node, user) => {
  return [
    ...members,
    createPerson('unknown', user._id, members, relationship.otherAffected)
  ];
};

// Convenience gender-typed aliases used by the keyboard layer.
export const addSon = (members, node, user, count = 1) =>
  addChildren(members, node, user, GENDER.male, count);
export const addDaughter = (members, node, user, count = 1) =>
  addChildren(members, node, user, GENDER.female, count);
export const addBrother = (members, node, user, count = 1) =>
  addSiblings(members, node, user, GENDER.male, count);
export const addSister = (members, node, user, count = 1) =>
  addSiblings(members, node, user, GENDER.female, count);
